import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardSection } from '@/components/Dashboard/enums/shared';
import { MENU_NAMES } from '@/consts/menu-names.consts';
import { useIsFigmaEmbedMode } from '@/contexts/FigmaEmbedModeContext';
import { useExportWorkflowLineage } from '@/hooks/useExportWorkflowLineage';
import { useIsWorkflowLineageEnabled } from '@/hooks/useIsWorkflowLineageEnabled';
import { I18N_KEYS } from '@/language/keys';
import { useUserStore } from '@/state/user.state';
import { WorkspaceRole } from '@/types/auth.types';
import type { AuditTrailExportActor } from '@/consts/analytics.consts';
import type { MenuAction } from '@/components/Menu/Actions';
import type { DashboardWorkflow } from '@/types/workflows';

const EXPORTABLE_SECTIONS = [DashboardSection.RecentRecipes, DashboardSection.WorkspaceFiles];

type AuditTrailCard = Pick<DashboardWorkflow, 'id' | 'name' | 'owner'>;

export function useAuditTrailCardMenuItem({
  section,
}: {
  section: DashboardSection;
}): (workflow: AuditTrailCard | undefined) => MenuAction[] {
  const { t } = useTranslation();
  const isExportWorkflowLineageEnabled = useIsWorkflowLineageEnabled();
  const isFigmaEmbedMode = useIsFigmaEmbedMode();
  const isWorkspaceAdmin = useUserStore((state) => state.user?.activeWorkspace?.role) === WorkspaceRole.Admin;
  const currentUserId = useUserStore((state) => state.user?.uid);
  const exportWorkflowLineage = useExportWorkflowLineage('file_browser');

  return useCallback(
    (workflow) => {
      if (
        !isExportWorkflowLineageEnabled ||
        isFigmaEmbedMode ||
        !EXPORTABLE_SECTIONS.includes(section) ||
        workflow === undefined
      ) {
        return [];
      }

      const isCardOwner = currentUserId !== undefined && workflow.owner === currentUserId;
      if (!isWorkspaceAdmin && !isCardOwner) {
        return [];
      }

      const actorRole: AuditTrailExportActor = isWorkspaceAdmin ? 'admin' : 'owner';

      return [
        { type: 'divider' },
        {
          biEventKey: { menu: MENU_NAMES.AUDIT_TRAIL_CARD, action: 'export' },
          name: t(I18N_KEYS.MAIN_DASHBOARD.CONTEXT_MENU.EXPORT_AUDIT_TRAIL),
          withLoading: true,
          action: async () => {
            await exportWorkflowLineage({ workflowId: workflow.id, workflowName: workflow.name, actorRole });
          },
        },
      ];
    },
    [
      isExportWorkflowLineageEnabled,
      isFigmaEmbedMode,
      isWorkspaceAdmin,
      currentUserId,
      section,
      exportWorkflowLineage,
      t,
    ],
  );
}
